import { FC, useMemo } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import {
  SortDir,
  SortableTableHeader,
  TableFrame,
  TablePagination,
  TableTooltipLabel,
  compareBigInts,
  useTablePagination,
  useTableSort,
  useTranslation,
} from "rujira.ui";
import {
  BorrowPositionsTableFragment$data,
  BorrowPositionsTableFragment$key,
} from "./__generated__/BorrowPositionsTableFragment.graphql";
import { PositionRow } from "./Position";
import { getCollateralTableColumns } from "../../common/collateralTableColumns";

const SORT_KEYS = [
  "collateral",
  "borrowed",
  "interestRate",
  "adjustedLtv",
  "liquidation",
] as const;

type SortKey = (typeof SORT_KEYS)[number];

const COLUMNS = [
  { id: "logo", width: "2.25rem" },
  { id: "collateral", width: "auto", minWidth: "9rem" },
  { id: "borrowed", width: "auto", minWidth: "13rem" },
  { id: "interest-rate", width: "auto", minWidth: "7rem" },
  { id: "adjusted-ltv", width: "auto", minWidth: "10rem" },
  { id: "liquidation", width: "auto", minWidth: "17rem" },
  { id: "action", width: "7rem", minWidth: "7rem" },
] as const;

type BorrowPositionsTableAccount = BorrowPositionsTableFragment$data[number];

const fragment = graphql`
  fragment BorrowPositionsTableFragment on GhostCreditAccount
  @relay(plural: true) {
    account {
      address
    }
    collaterals {
      collateral {
        __typename
        ... on Balance {
          asset {
            metadata {
              symbol
            }
          }
        }
      }
    }
    debts {
      debt {
        borrower {
          vault {
            status {
              debtRate
            }
          }
        }
      }
    }
    ltv
    collateralValueUsd
    debtValueUsd
    collateralLiquidationValueUsd
    debtLiquidationValueUsd
    ...PositionBorrowRowFragment
  }
`;

const liquidationDistance = (account: BorrowPositionsTableAccount): bigint => {
  const isStablecoinCollateral = account.collaterals.every(
    ({ collateral }) =>
      collateral.__typename === "Balance" &&
      ["USDC", "USDT"].includes(collateral.asset.metadata.symbol)
  );

  if (isStablecoinCollateral) {
    if (account.debtValueUsd === 0n) return 0n;
    const delta =
      ((account.debtLiquidationValueUsd - account.debtValueUsd) * 10n ** 12n) /
      account.debtValueUsd;
    return delta < 0n ? 0n : delta;
  }

  if (account.collateralValueUsd === 0n) return 0n;
  const delta =
    ((account.collateralValueUsd - account.collateralLiquidationValueUsd) *
      10n ** 12n) /
    account.collateralValueUsd;
  return delta < 0n ? 0n : delta;
};

const sortValue = (
  account: BorrowPositionsTableAccount,
  sortBy: SortKey
): bigint => {
  switch (sortBy) {
    case "collateral":
      return account.collateralValueUsd;
    case "borrowed":
      return account.debtValueUsd;
    case "interestRate":
      return account.debts[0]?.debt.borrower.vault.status.debtRate ?? 0n;
    case "adjustedLtv":
      return account.ltv;
    case "liquidation":
      return liquidationDistance(account);
  }
};

export const BorrowPositionsTable: FC<{
  accounts: BorrowPositionsTableFragment$key;
  adjustmentThreshold: bigint;
}> = ({ accounts, adjustmentThreshold }) => {
  const { t } = useTranslation("borrow");
  const data = useFragment<BorrowPositionsTableFragment$key>(
    fragment,
    accounts
  );
  const columns = useMemo(
    () => getCollateralTableColumns(COLUMNS, data),
    [data]
  );
  const { sortBy, sortDir, toggleSort } = useTableSort<SortKey>({
    initialBy: "adjustedLtv",
    initialDir: SortDir.Desc,
    defaultDir: () => SortDir.Desc,
  });
  const sortedAccounts = useMemo(
    () =>
      [...data].sort((a, b) => {
        const comparison = compareBigInts(
          sortValue(a, sortBy),
          sortValue(b, sortBy),
          sortDir
        );
        return comparison || a.account.address.localeCompare(b.account.address);
      }),
    [data, sortBy, sortDir]
  );
  const { pageRows: visibleAccounts, pagination } = useTablePagination(
    sortedAccounts,
    { resetKey: `${sortBy}:${sortDir}` }
  );
  const sortHeaderProps = { active: sortBy, dir: sortDir, onSort: toggleSort };

  return (
    <>
      <TableFrame
        className="borrow-positions-table collateral-table"
        columns={columns}
        stickyFirstColumn
        tableClassName="table table--fixed table--big condensed mb-0">
        <thead>
          <tr>
            <th aria-hidden="true" />
            <SortableTableHeader
              {...sortHeaderProps}
              value="collateral"
              label={t("deposited")}
            />
            <SortableTableHeader
              {...sortHeaderProps}
              value="borrowed"
              label={t("borrowed")}
            />
            <SortableTableHeader
              {...sortHeaderProps}
              value="interestRate"
              align="center"
              label={
                <TableTooltipLabel
                  label={t("interestRate")}
                  tooltip={`<div class="w-30 text-left">${t("interestRateTooltip")}</div>`}
                />
              }
            />
            <SortableTableHeader
              {...sortHeaderProps}
              value="adjustedLtv"
              align="center"
              label={
                <TableTooltipLabel
                  label={t("adjustedLtv")}
                  tooltip={`<div class="w-30 text-left">${t("adjustedLtvTooltip")}</div>`}
                />
              }
            />
            <SortableTableHeader
              {...sortHeaderProps}
              value="liquidation"
              label={
                <TableTooltipLabel
                  label={t("liquidationPriceTableHeader")}
                  tooltip={`<div class="w-30 text-left">${t("liquidationPriceTableTooltip")}</div>`}
                />
              }
            />
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {visibleAccounts.map((account) => (
            <PositionRow
              k={account}
              key={account.account.address}
              adjustmentThreshold={adjustmentThreshold}
            />
          ))}
        </tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  );
};
